import api from './index';
import type {
  Message,
  MessageListItem,
  UpdateMessageRequest,
  SendMessageResponse,
  ReplyMessageResponse,
  AssistantResponse,
} from '@/types/message';
import type { MessagesResponse } from '@/types/api';

// =============================================================================
// REST operations (via API Gateway)
// =============================================================================

/**
 * List all messages (chat sessions) for the current user.
 */
export async function getMessages(params?: {
  status?: string;
  limit?: number;
  cursor?: string;
}): Promise<MessagesResponse> {
  const response = await api.get<MessagesResponse>('/messages', { params });
  return response.data;
}

/**
 * Get a specific message with full conversation history.
 */
export async function getMessage(messageId: string): Promise<Message> {
  const response = await api.get<Message>(`/messages/${messageId}`);
  return response.data;
}

/**
 * Update message metadata (e.g., mark as read, archive).
 */
export async function updateMessage(
  messageId: string,
  data: UpdateMessageRequest,
): Promise<MessageListItem> {
  const response = await api.patch<MessageListItem>(`/messages/${messageId}`, data);
  return response.data;
}

// =============================================================================
// Two-step message send (Step 1 via API Gateway, Step 2 via Lambda Function URL)
// =============================================================================

/**
 * Step 1: Create a new message (POST /messages).
 * Saves the user's message to DynamoDB and returns a stream_url
 * for fetching the assistant's response.
 */
export async function createMessage(content: string): Promise<SendMessageResponse> {
  const response = await api.post<SendMessageResponse>('/messages', { content });
  return response.data;
}

/**
 * Step 1: Reply to an existing message (POST /messages/{id}/reply).
 * Saves the user's reply to DynamoDB and returns a stream_url
 * for fetching the assistant's response.
 */
export async function replyToMessage(
  messageId: string,
  content: string,
): Promise<ReplyMessageResponse> {
  const response = await api.post<ReplyMessageResponse>(
    `/messages/${messageId}/reply`,
    { content },
  );
  return response.data;
}

/**
 * Step 2: Fetch the assistant's complete response from the Lambda Function URL.
 *
 * The stream_url is a Lambda Function URL with a signed token in the query
 * string. The handler generates the assistant's complete response and returns
 * it as JSON.
 *
 * Uses fetch() directly (not axios) because the URL is an absolute Lambda
 * Function URL, not a path relative to the API Gateway base.
 */
export async function fetchAssistantResponse(
  streamUrl: string,
): Promise<AssistantResponse> {
  const response = await fetch(streamUrl, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = 'Failed to get response';
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.error?.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();

  // Check for application-level error in the response body
  if (data.error) {
    throw new Error(data.error.message || 'An error occurred');
  }

  return data as AssistantResponse;
}
